import React, { useContext } from 'react';
import {
  AlertBox,
  AlertBoxDismissButton,
  Button,
  Column,
  FlexBox,
  FormField,
  FormInputGroup,
  GridContainer,
  H2,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogNav,
  Row,
  Spinner,
  StandardForm,
  TextArea,
  Typography,
  useScreenClass,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { useMutation } from 'react-query';
import {
  submitCustomerFeedbackGroup,
  submitCustomerFeedbackGroupForAnonymousOrder,
} from '../../../apis/orderUpdateRequestsApi';
import useSiteData from '../../../hooks/useSiteData';
import { getShopperId } from '../../../util/auth';
import DesignFeedbackGroupRequestContext from '../../contexts/DesignFeedbackGroupRequestContext';
import { DesignFeedbackGroupResponseContext } from '../../contexts/DesignFeedbackGroupResponseContext';
import { ExpertCard } from './ExpertCard';
import { ErrorBanner } from './ErrorBanner';

export const RequestChangeModal = ({ isModalOpen, setIsModalOpen }) => {
  const screenClass = useScreenClass();
  const isMobile = ['xs'].includes(screenClass);

  const [designFeedbackMessages, sharedActionMessages] = useTranslations([
    'designFeedbackMessages',
    'sharedActionMessages',
  ]);
  const { tenantEnvironment, vistacartTenant } = useSiteData();
  const shopperId = getShopperId();

  const { orderPlacedAnonymously, orderId, groupId, emailAddress } = useContext(
    DesignFeedbackGroupRequestContext
  );

  const {
    requestedChanges,
    setRequestedChanges,
    selectedStatus,
    isRejected,
    selectedDesignOptionId,
    resetCustomerFeedback,
    setIsFeedbackSubmitted,
  } = useContext(DesignFeedbackGroupResponseContext);

  /**
   * Mutations are used to create/update/delete data or perform server side-effects.
   * For more about mutations: https://tanstack.com/query/v4/docs/guides/mutations
   */
  const {
    mutate: submitFeedback,
    isLoading: isFeedbackSubmissionLoading,
    isError: isFeedbackSubmissionFailed,
    reset,
  } = useMutation(
    async () => {
      const feedbackSubmission = {
        status: selectedStatus,
        selectedOptionId: selectedDesignOptionId,
        customerRemarks: requestedChanges,
      };

      const trackingData = {
        groupId,
        orderNumber: orderId,
        selectedStatus,
        selectedOptionId: selectedDesignOptionId,
        requestedChanges,
        environment: tenantEnvironment,
        tenant: vistacartTenant,
      };

      if (orderPlacedAnonymously) {
        await submitCustomerFeedbackGroupForAnonymousOrder({
          orderNumber: orderId,
          groupId,
          email: emailAddress,
          feedbackSubmission,
          environment: tenantEnvironment,
          trackingData,
        });
      } else {
        await submitCustomerFeedbackGroup({
          orderNumber: orderId,
          groupId,
          shopperId,
          feedbackSubmission,
          environment: tenantEnvironment,
          trackingData,
        });
      }
    },
    {
      onSuccess: () => {
        setIsModalOpen(false);
        setIsFeedbackSubmitted(true);
      },
    }
  );

  const onCloseHandler = () => {
    // Close modal
    setIsModalOpen(false);
    // Clear the previous feedback
    resetCustomerFeedback();
    // Reset the mutation back to its initial state so it is ready for the next selection
    reset();
  };

  // Disable the "Submit"/"Request changes" button while an existing submission is in progress OR
  // the user has not yet entered any input into the "request changes" text area
  const isSubmitDisabled =
    isFeedbackSubmissionLoading ||
    (isRejected && (!requestedChanges || requestedChanges.length === 0));

  // Disable the "Cancel" button while an existing submission is in progress
  const isCancelDisabled = isFeedbackSubmissionLoading;

  const modalContentMain = (
    <GridContainer>
      <Row>
        <Column span={12}>
          <H2 mb={6}>{designFeedbackMessages.requestChanges}</H2>
          <Typography mb={3} pb={6}>
            <AlertBox skin="warning">
              {designFeedbackMessages.deliveryDelayReminder}
              <AlertBoxDismissButton accessibleText="Dismiss alert" />
            </AlertBox>
          </Typography>
          <Typography mt={3} mb={3} id="request-changes-instructions">
            {designFeedbackMessages.explainChangesMessage}
          </Typography>
          <StandardForm skin="standard" mt={3} mb={3}>
            <FormField>
              <FormInputGroup>
                <TextArea
                  style={{ width: '100%' }}
                  onChange={event => {
                    setRequestedChanges(event.target.value.trim());
                  }}
                  rows={0}
                  aria-labelledby="request-changes-instructions"
                  placeholder={designFeedbackMessages.typeDescriptionOfChanges}
                >
                  {requestedChanges}
                </TextArea>
              </FormInputGroup>
            </FormField>
          </StandardForm>
          <ExpertCard
            title={designFeedbackMessages.expertTips}
            messages={[
              designFeedbackMessages.expertTip1,
              designFeedbackMessages.expertTip2,
            ]}
            orderedList
          />
        </Column>
      </Row>
    </GridContainer>
  );

  const cancelButton = (
    <Button
      width="full-width"
      skin="secondary"
      onClick={onCloseHandler}
      disabled={isCancelDisabled}
    >
      {sharedActionMessages.cancel}
    </Button>
  );

  const modalContentFooter = (
    <GridContainer>
      <Row>
        <Column span={12}>
          <ModalDialogButtons>
            <GridContainer>
              <Row>
                <Column span={12}>
                  <FlexBox
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap="between-actions"
                  >
                    {isMobile ? null : cancelButton}
                    <Button
                      width="full-width"
                      skin="primary"
                      onClick={() => {
                        submitFeedback();
                      }}
                      disabled={isSubmitDisabled}
                    >
                      {isFeedbackSubmissionLoading ? (
                        <Spinner size="tiny" accessibleText="Submitting" />
                      ) : null}
                      {designFeedbackMessages.submitChanges}
                    </Button>
                    {isMobile && isFeedbackSubmissionFailed ? (
                      <Column span={12}>
                        <ErrorBanner />
                      </Column>
                    ) : null}
                    {isMobile ? cancelButton : null}
                  </FlexBox>
                </Column>
              </Row>
            </GridContainer>
          </ModalDialogButtons>
        </Column>
      </Row>
      {!isMobile && isFeedbackSubmissionFailed ? (
        <Row mt={6}>
          <Column span={12}>
            <ErrorBanner />
          </Column>
        </Row>
      ) : null}
    </GridContainer>
  );

  const modalContent = (
    <>
      <ModalDialogBody>
        <GridContainer>
          <Row>
            <Column span={12}>{modalContentMain}</Column>
          </Row>
        </GridContainer>
      </ModalDialogBody>
      {isMobile ? (
        <ModalDialogFooter>{modalContentFooter}</ModalDialogFooter>
      ) : (
        <ModalDialogFooter pinned>{modalContentFooter}</ModalDialogFooter>
      )}
    </>
  );

  return (
    <ModalDialog
      variant="panel-right"
      bodyWidth="capped"
      isOpen={isModalOpen}
      onRequestDismiss={onCloseHandler}
      onlyRenderWhenOpen
    >
      <ModalDialogContent>
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={sharedActionMessages.close} />
        </ModalDialogNav>
        {modalContent}
      </ModalDialogContent>
    </ModalDialog>
  );
};
